import { useTranslation } from "react-i18next";

import {
    PATIENT_CANCELLATION_HOUR_CHOICES,
    SchedulerSettings,
    SchedulerSettingsUpdate,
} from "../../domain/types";

/**
 * Whether patients may cancel their own appointments, and how late.
 *
 * Beside patient booking because it is the same kind of decision — how much
 * of their own diary a patient can manage without writing to the therapist —
 * and saved on change like everything else on this tab.
 */
export default function PatientCancellationSettings({
    settings,
    isSaving,
    onChange,
}: {
    settings: SchedulerSettings;
    isSaving: boolean;
    onChange: (update: SchedulerSettingsUpdate) => void;
}) {
    const { t } = useTranslation();

    return (
        <section className="flex max-w-2xl flex-col gap-2">
            <h2 className="text-base font-semibold">
                {t("scheduler.settings.patientCancellation.title")}
            </h2>
            <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <label className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        className="mt-1"
                        checked={settings.patient_cancellation_enabled}
                        disabled={isSaving}
                        aria-label={t("scheduler.settings.patientCancellation.toggle")}
                        onChange={(event) =>
                            onChange({ patient_cancellation_enabled: event.target.checked })
                        }
                    />
                    <span className="flex flex-col gap-1 text-sm">
                        <span className="font-medium">
                            {t("scheduler.settings.patientCancellation.toggle")}
                        </span>
                        <span className="text-content-tertiary">
                            {t("scheduler.settings.patientCancellation.hint")}
                        </span>
                    </span>
                </label>

                {settings.patient_cancellation_enabled && (
                    <label className="flex max-w-xs flex-col gap-1 pl-7 text-sm">
                        {t("scheduler.settings.patientCancellation.notice")}
                        <select
                            value={settings.patient_cancellation_min_hours}
                            disabled={isSaving}
                            aria-label={t("scheduler.settings.patientCancellation.notice")}
                            onChange={(event) =>
                                onChange({
                                    patient_cancellation_min_hours: Number(event.target.value),
                                })
                            }
                            className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                        >
                            {/* A notice set through the API, or left from an older
                                list, still has to show as what it is. */}
                            {noticeChoices(settings.patient_cancellation_min_hours).map((hours) => (
                                <option key={hours} value={hours}>
                                    {hours === 0
                                        ? t("scheduler.settings.patientCancellation.noticeNone")
                                        : t("scheduler.settings.patientCancellation.noticeOption", {
                                              count: hours,
                                          })}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
        </section>
    );
}

/** The offered notice periods, plus the saved one when it is not among them. */
function noticeChoices(current: number): number[] {
    const choices: number[] = [...PATIENT_CANCELLATION_HOUR_CHOICES];
    if (!choices.includes(current)) {
        choices.push(current);
    }
    return choices.sort((a, b) => a - b);
}
